import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Header } from "../components/layout/Header";
import { PluginCard, PluginCardSkeleton } from "../components/plugins/PluginCard";
import { marketplaceApi, pluginApi, type MarketplaceWithPlugins } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { UserIcon } from "../components/ui/Icons";

export function PublicMarketplacePage() {
  const { slug } = useParams<{ slug: string }>();
  const { isAuthenticated } = useAuth();
  const [marketplace, setMarketplace] = useState<MarketplaceWithPlugins | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!slug) return;
      try {
        const data = await marketplaceApi.getPublicMarketplace(slug);
        setMarketplace(data);
      } catch {
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [slug]);

  // Load user favorites if authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    pluginApi
      .getUserFavorites()
      .then((ids) => setFavoriteIds(new Set(ids)))
      .catch(() => {});
  }, [isAuthenticated]);

  // Extract unique categories from plugins, preserving order
  const categories = useMemo(() => {
    if (!marketplace) return [];
    const seen = new Set<string>();
    const cats: string[] = [];
    for (const plugin of marketplace.plugins) {
      if (plugin.category && !seen.has(plugin.category)) {
        seen.add(plugin.category);
        cats.push(plugin.category);
      }
    }
    return cats;
  }, [marketplace]);

  const hasCategories = categories.length > 0;

  // Filter plugins by active category
  const filteredPlugins = useMemo(() => {
    if (!marketplace) return [];
    if (!hasCategories || activeCategory === null) return marketplace.plugins;
    return marketplace.plugins.filter((p) => p.category === activeCategory);
  }, [marketplace, activeCategory, hasCategories]);

  const handleStar = async (pluginId: string) => {
    if (!isAuthenticated) return;
    try {
      const result = await pluginApi.toggleFavorite(pluginId);
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (result.favorited) {
          next.add(pluginId);
        } else {
          next.delete(pluginId);
        }
        return next;
      });
    } catch (err) {
      console.error("Failed to toggle favorite:", err);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-base">
        <Header />
        <main className="relative pt-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="animate-pulse">
              <div className="mb-2 h-10 w-64 rounded bg-elevated/50" />
              <div className="mb-6 h-4 w-96 rounded bg-elevated/50" />
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((i) => (
                  <PluginCardSkeleton key={i} />
                ))}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (notFound || !marketplace) {
    return (
      <div className="min-h-screen bg-base">
        <Header />
        <div className="pointer-events-none fixed inset-0 overflow-hidden">
          <div className="absolute -right-1/4 top-0 h-[600px] w-[600px] rounded-full bg-amber/5 blur-[120px]" />
          <div className="absolute inset-0 bg-grid opacity-20" />
        </div>
        <main className="relative flex min-h-[60vh] flex-col items-center justify-center pt-24">
          <h1 className="mb-4 font-display text-4xl font-bold text-primary">404</h1>
          <p className="mb-6 text-muted">This marketplace doesn't exist or has been removed.</p>
          <Link to="/" className="btn-primary">
            Browse All Plugins
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base">
      <Header />

      {/* Background effects */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-1/4 top-0 h-[600px] w-[600px] rounded-full bg-amber/5 blur-[120px]" />
        <div className="absolute -left-1/4 bottom-0 h-[600px] w-[600px] rounded-full bg-purple-500/5 blur-[120px]" />
        <div className="absolute inset-0 bg-grid opacity-20" />
      </div>

      <main className="relative pt-24">
        <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
          {/* Marketplace Header */}
          <div className="mb-8">
            <h1 className="font-display text-4xl font-bold tracking-tight text-primary">
              {marketplace.name}
            </h1>
            {marketplace.description && (
              <p className="mt-3 max-w-2xl text-lg text-secondary">{marketplace.description}</p>
            )}
            <div className="mt-4 flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-amber to-amber-hover">
                <UserIcon size={14} className="text-black" />
              </div>
              <span className="font-mono text-sm text-muted">
                Curated by <span className="text-secondary">{marketplace.owner.name}</span>
              </span>
              <span className="font-mono text-sm text-muted">
                {marketplace.plugins.length}{" "}
                {marketplace.plugins.length === 1 ? "plugin" : "plugins"}
              </span>
            </div>
          </div>

          {/* Category Tabs */}
          {hasCategories && (
            <div className="mb-8 flex flex-wrap gap-2">
              <button
                onClick={() => setActiveCategory(null)}
                className={`rounded-full px-4 py-2 font-mono text-sm transition-colors ${
                  activeCategory === null
                    ? "bg-amber text-black"
                    : "bg-elevated text-secondary hover:bg-elevated/80 hover:text-primary"
                }`}
              >
                All ({marketplace.plugins.length})
              </button>
              {categories.map((cat) => {
                const count = marketplace.plugins.filter((p) => p.category === cat).length;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`rounded-full px-4 py-2 font-mono text-sm transition-colors ${
                      activeCategory === cat
                        ? "bg-amber text-black"
                        : "bg-elevated text-secondary hover:bg-elevated/80 hover:text-primary"
                    }`}
                  >
                    {cat} ({count})
                  </button>
                );
              })}
            </div>
          )}

          {/* Plugins Grid */}
          {filteredPlugins.length === 0 ? (
            <div className="card-glow flex flex-col items-center justify-center py-20 text-center">
              <p className="text-muted">This marketplace doesn't have any plugins yet.</p>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filteredPlugins.map((plugin) => (
                <PluginCard
                  key={plugin.id}
                  id={plugin.id}
                  slug={plugin.slug}
                  name={plugin.name}
                  description={plugin.description}
                  author={plugin.author.name}
                  tags={plugin.tags}
                  downloads={plugin._count.downloads}
                  stars={plugin._count.favorites}
                  featured={plugin.featured}
                  starred={favoriteIds.has(plugin.id)}
                  files={plugin.files}
                  onStar={isAuthenticated ? handleStar : undefined}
                  pricingModel={plugin.pricingModel}
                  price={plugin.price}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
